import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { StatusCodes } from 'http-status-codes';
import styled from 'styled-components';

import request from '../../../../services/request';
import KendoService from '../../../../services/logic/KendoService';
import ErrorLabel from '../ErrorLabel';
import { transformLabels } from '../../../../utils/misc';
import { Colors } from '../../../../scss/common/style';

import tick from '../../../../static/img/tick.svg';
import plus from '../../../../static/img/plus.svg';

interface Props {
  name: string;
  initialValue?: any;
  onChange?: any;
  getOptionLabel: any;
  queryFields: any;
  url: string;
  isMulti?: boolean;
  cols?: number;
  errorMessage: string;
  label: string;
  disabled?: boolean;
  height?: string;
  additionalFilters?: Record<string, any>;
}

export default function AutocompletePaginatedComponent({
  url,
  name,
  initialValue,
  onChange,
  getOptionLabel,
  queryFields = ['name'],
  cols = 6,
  errorMessage = '',
  label,
  disabled = false,
  height = '33px',
  additionalFilters = {},
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [initialObject, setInitialObject] = useState(null);
  const [selectedItems, setSelectedItems] = useState(initialValue || null);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const skipRef = React.useRef(0);

  const loadMore = async () => {
    if (!hasMore || isFetchingMore) return;

    setIsFetchingMore(true);

    const filters = queryFields.length
      ? KendoService.buildFilter(queryFields, '')
      : [];

    if (additionalFilters) {
      Object.entries(additionalFilters).forEach(([key, val]) => {
        filters.push({
          field: key,
          value: val,
          operator: 'eq',
        });
      });
    }

    const result = await request(url, 'post', {
      filter: { filters, logic: 'and' },
      take: 20,
      skip: skipRef.current,
    });

    if (result.status === StatusCodes.OK) {
      const newOptions = result.data.data;
      setOptions((prev) => [...(prev || []), ...newOptions]);
      setSkip((prevSkip) => {
        const newSkip = prevSkip + 20;
        skipRef.current = newSkip;
        return newSkip;
      });
      if (newOptions.length < 20) {
        setHasMore(false);
      }
    }

    setIsFetchingMore(false);
  };

  const loadData = async (q) => {
    setIsLoading(true);

    const filters = KendoService.buildFilter(queryFields, q);

    if (initialValue) {
      filters.push({
        field: 'id',
        value: initialValue,
        operator: 'eq',
      });
    }

    const result = await request(url, 'post', {
      filter: {
        filters,
        logic: 'or',
      },
      take: 20,
      skip: 0,
    });

    setIsLoading(false);

    const uniqueOptions = result.status === StatusCodes.OK
      ? Array.from(new Map(result.data.data.map((item) => [item.id, item])).values())
      : [];

    return uniqueOptions;
  };

  useEffect(() => {
    const getInitial = async () => {
      setIsLoading(true);

      const filters = [];
      if (initialValue) {
        filters.push({
          field: 'id',
          value: initialValue,
          operator: 'eq',
        });
      }

      const result = await request(url, 'post', {
        filter: {
          filters,
          logic: 'or',
        },
        take: 20,
        skip: 0,
      });

      if (result.status === StatusCodes.OK) {
        const data = result.data.data;

        setOptions(data);
        setSkip(data.length);
        setHasMore(data.length === 20);
        if (data) {
          setInitialObject(data.find((element) => element.id === initialValue));
        }
      }

      setIsLoading(false);
    };

    getInitial();
  }, [url, initialValue]);

  useEffect(() => {
    setSkip(0);
    skipRef.current = 0;
    setHasMore(true);
    setOptions([]);
  }, [additionalFilters?.countryId]);

  const onChangeLocal = (selected) => {
    setSelectedItems(selected);
    if (Array.isArray(selected)) {
      onChange(name, selected);
    } else {
      onChange(name, selected?.id || '');
    }
  };

  return (
    <WrapperSelect className={`col-sm-${cols}`}>
      {label && <StyledLabel htmlFor={name}>{label}</StyledLabel>}
      {options && initialObject !== null && (
        <StyledSelect
          isClearable
          height={height}
          isDisabled={disabled}
          getOptionLabel={getOptionLabel}
          getOptionValue={(option) => option.id}
          defaultValue={initialObject}
          defaultOptions={options}
          loadOptions={loadData}
          onChange={onChangeLocal}
          onMenuScrollToBottom={loadMore}
          styles={{
            menu: (provided, state) => ({
              ...provided,
              zIndex: 9999,
              backgroundColor: state.isSelected ? Colors.darkBrown : '#FBFBFB!important',
              color: state.isSelected ? '#fff!important' : 'black!important',
              height: 'auto !important',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected ? Colors.darkBrown : '#FBFBFB',
              color: state.isSelected ? '#fff!important' : Colors.darkBrown,
              '&:hover': {
                backgroundColor: '#eeee !important',
                color: Colors.darkBrown,
              },
              alignItems: 'center',
              padding: 10,
            }),
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderRadius: 20,
              zIndex: 10,
              minHeight: `${height} !important`,
              alignItems: 'center',
              boxShadow: 'none',
              border: '1px solid #EEEEEE !important',
              '&:hover': {
                border: '1px solid #EEEEEE !important',
              },
              '&:focus': {
                border: '1px solid #EEEEEE !important',
              },
              '&:active': {
                border: '1px solid #EEEEEE !important',
              },
            }),
            dropdownIndicator: (provided, state) => ({
              ...provided,
              color: Colors.newOrage,
              height: 31,
              alignItems: 'center',
              '&:hover': {
                color: Colors.newOrage,
              },
              '&:active': {
                color: Colors.newOrage,
              },
              '&:focus': {
                color: Colors.newOrage,
              },
            }),
          }}
        />
      )}
      {errorMessage && <ErrorLabel error={errorMessage} />}
    </WrapperSelect>
  );
}

const WrapperSelect = styled.div`
  height: 100%;
`;

const StyledLabel = styled.label`
  font-size: 15px;
  color: ${Colors.darkBrown};
  font-weight: 700;
  line-height: 17px;
  margin-left: 8px;
  margin-bottom: 11px;
`;

const StyledSelect = styled(AsyncSelect)`
  position: relative;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;

  &:active {
    box-shadow: none !important;
    border-color: #eeeeee !important;
  }
  &:hover {
    box-shadow: none !important;
    border-color: #eeeeee !important;
  }
  &:focus {
    box-shadow: none !important;
    border-color: #eeeeee !important;
  }
  &:target {
    box-shadow: none !important;
    border-color: #eeeeee !important;
  }
  &:visited {
    box-shadow: none !important;
    border-color: #eeeeee !important;
  }
`;